import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import {
  SiteConfig,
  SiteImages,
  MaintenancePlansConfig,
  defaultConfig,
  defaultMaintenancePlans,
  getDefaultImages,
} from "@/lib/site-config";

interface SiteConfigContextValue {
  config: SiteConfig;
  images: SiteImages;
  plans: MaintenancePlansConfig;
}

// BASE_URL is baked in by Vite at build time.
// For the root-domain build (swiftmobilemechanics.com): BASE_URL = "/"
// It always ends with "/".
const BASE = import.meta.env.BASE_URL;

const SiteConfigContext = createContext<SiteConfigContextValue>({
  config: defaultConfig,
  images: getDefaultImages(),
  plans: defaultMaintenancePlans,
});

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(defaultConfig);
  // Initialize with correctly-prefixed defaults — no broken images on first paint
  const [images, setImages] = useState<SiteImages>(getDefaultImages());
  const [plans, setPlans] = useState<MaintenancePlansConfig>(defaultMaintenancePlans);

  useEffect(() => {
    const loadConfig = () => {
      fetch(`${BASE}config/site.json`)
        .then((r) => r.json())
        .then((data: SiteConfig) => setConfig(data))
        .catch(() => {});

      fetch(`${BASE}config/images.json`)
        .then((r) => r.json())
        .then((data: Record<string, string>) => {
          const prefixed = Object.fromEntries(
            Object.entries(data).map(([k, v]) => {
              const cleanPath = v.replace(/^\/+/, "");
              return [k, `${BASE}${cleanPath}`];
            })
          ) as unknown as SiteImages;
          setImages({ ...getDefaultImages(), ...prefixed });
        })
        .catch(() => {});

      fetch(`${BASE}config/maintenance-plans.json`)
        .then((r) => r.json())
        .then((data: MaintenancePlansConfig) => setPlans(data))
        .catch(() => {});
    };

    const timer = window.setTimeout(loadConfig, 4000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <SiteConfigContext.Provider value={{ config, images, plans }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
